import { Spinner } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect, useRef } from "react";
import z from "zod";

import { useStrapiPageRenderer } from "@/components/custom-pages/hooks/use-strapi-page-renderer";
import { valuesToSearchRecord } from "@/components/custom-pages/hooks/use-strapi-page-values";
import { StrapiPageRenderer } from "@/components/custom-pages/strapi-page-renderer";
import { resolvePageParams } from "@/components/custom-pages/utils/resolve-page-params";
import { usePageTitleOverride } from "@/providers/hooks/use-page-title-override";
import { useStrapiPageBySlug } from "@/queries/strapi/page.queries";
import { APP_ROUTES_FULL } from "@/utils/routes";

const CustomPageParamsSchema = z.object({
    slug: z.string(),
});

const CustomPageSearchSchema = z.object({}).catchall(z.union([z.string(), z.number(), z.boolean()]));

export const Route = createFileRoute(APP_ROUTES_FULL.CUSTOM_PAGE)({
    component: CustomPage,
    params: {
        parse: (rawParams) => CustomPageParamsSchema.parse(rawParams),
        stringify: (params) => ({ slug: params.slug }),
    },
    validateSearch: zodValidator(CustomPageSearchSchema),
    staticData: {
        config: {
            title: "custom_page",
        },
    },
});

function CustomPage() {
    const { slug } = Route.useParams();
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const { data: page, isLoading } = useStrapiPageBySlug(slug);

    usePageTitleOverride(page?.name);

    const onSearchChange = useCallback(
        (nextSearch: Record<string, string | number | boolean | undefined>) => {
            const searchParams = Object.fromEntries(
                Object.entries(nextSearch).filter(
                    (entry): entry is [string, string | number | boolean] => entry[1] !== undefined
                )
            );

            navigate({
                to: Route.to,
                params: { slug },
                search: searchParams,
                replace: true,
            });
        },
        [navigate, slug]
    );

    const { values, updateValues } = useStrapiPageRenderer({
        schema: page?.queryParams,
        search,
        onSearchChange,
    });

    const syncedSlugRef = useRef<string | null>(null);

    useEffect(() => {
        if (!page?.queryParams || syncedSlugRef.current === slug) {
            return;
        }

        syncedSlugRef.current = slug;

        const resolvedValues = resolvePageParams(page.queryParams, new URLSearchParams(window.location.search));
        onSearchChange(valuesToSearchRecord(resolvedValues));
    }, [slug, page?.queryParams, onSearchChange]);

    if (isLoading || !page?.queryParams || !page.documentId) {
        return (
            <div className="flex min-h-[320px] items-center justify-center">
                <Spinner className="size-6" />
            </div>
        );
    }

    return (
        <StrapiPageRenderer
            documentId={page.documentId}
            values={values}
            updateValues={updateValues}
            title={page?.description}
        />
    );
}
